// 착공일 변경 — 전 공정을 새 착공일로 다시 펼친다. 계약서 착공일은 건드리지 않는다(설계 결정 4).

import {
  lookupHolidays,
  storedHolidays,
  workRuleOf,
  type HolidayLoader,
} from "./build-from-contract";
import type { Project, ProjectPhase } from "./types";
import type { HolidayLookup } from "../quote/holidays";
import { assertStartDate, generateSchedule } from "../quote/schedule";

export type ProjectSchedulePatch = Pick<
  Project,
  "planned_start_date" | "planned_end_date" | "schedule_holidays"
>;

const DAY_MS = 86_400_000;

const shiftDate = (date: string, days: number): string =>
  new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);

// 펼칠 공정이 없으면 준공 예정일을 착공일이 옮긴 만큼 같이 민다(기간 유지). 옛 착공일이 없으면 새 착공일보다 앞설 때만 비운다.
export const shiftedEndDate = (
  project: Pick<Project, "planned_start_date" | "planned_end_date">,
  startDate: string
): string | null => {
  const { planned_start_date: from, planned_end_date: end } = project;
  if (end === null) return null;
  if (from === null) return end >= startDate ? end : null;
  const days = Math.round((Date.parse(`${startDate}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
  return shiftDate(end, days);
};

const byOrder = (phases: readonly ProjectPhase[]): ProjectPhase[] =>
  [...phases].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));

// 공정이 없으면 조회하지 않는다 — 펼칠 것이 없다.
export const holidaysForReschedule = async (
  project: Pick<Project, "work_holiday">,
  phases: readonly ProjectPhase[],
  startDate: string,
  load: HolidayLoader
): Promise<HolidayLookup | null> => {
  assertStartDate(startDate);
  if (phases.length === 0) return null;
  return lookupHolidays(
    startDate,
    phases.map((p) => p.duration_days),
    project.work_holiday,
    load
  );
};

export const buildReschedule = (
  project: Project,
  phases: readonly ProjectPhase[],
  startDate: string,
  holidays: HolidayLookup | null
): { project: ProjectSchedulePatch; phases: ProjectPhase[] } => {
  assertStartDate(startDate);
  const ordered = byOrder(phases);
  if (ordered.length === 0 || holidays === null) {
    return {
      project: {
        planned_start_date: startDate,
        planned_end_date: shiftedEndDate(project, startDate),
        schedule_holidays: project.schedule_holidays,
      },
      phases: ordered,
    };
  }
  const dates = new Map(
    generateSchedule({
      start_date: startDate,
      plans: ordered.map((p) => ({ phase_id: p.id, duration_days: p.duration_days })),
      work: workRuleOf(project),
      holidays: holidays.applied ? holidays.dates : new Set<string>(),
    }).map((d) => [d.phase_id, d])
  );
  const next = ordered.map((p) => ({
    ...p,
    start_date: dates.get(p.id)?.start_date ?? null,
    end_date: dates.get(p.id)?.end_date ?? null,
  }));
  const ends = next.map((p) => p.end_date).filter((d): d is string => d !== null).sort();
  return {
    project: {
      planned_start_date: startDate,
      planned_end_date: ends.length > 0 ? ends[ends.length - 1] : shiftedEndDate(project, startDate),
      schedule_holidays: storedHolidays(holidays),
    },
    phases: next,
  };
};

// 재배치 결과가 계약 준공일보다 늦으면 경고만 한다(설계 결정 4).
export const endsAfterContract = (
  plannedEnd: string | null,
  contractEnd: string | null
): boolean => plannedEnd !== null && contractEnd !== null && plannedEnd > contractEnd;
